import { spinner } from "@clack/prompts";
import { Cli, Openapi, z } from "incur";
import { createWhopFetch, filterSpecByTag, nativeSpec } from "../api/index.js";

const POLL_INTERVAL_MS = 3_000;

const whopFetch = createWhopFetch();

interface MediaAsset {
	id: string;
	media_type?: string;
	status: "processing" | "ready" | "failed";
	error_message?: string | null;
	amount_charged?: number | null;
	file?: { id: string; url: string } | null;
}

type CtaCommand = {
	command: string;
	description?: string;
	options?: Record<string, string>;
};

type RunCtx = {
	agent: boolean;
	options: Record<string, unknown>;
	error: (opts: {
		code: string;
		message: string;
		retryable?: boolean;
		cta?: { description?: string; commands: CtaCommand[] };
	}) => never;
};

/**
 * The media group is spec-driven except `generate`, which is hand-written to
 * add `--wait`: generation is async (the API returns a `processing` asset),
 * so waiting collapses the create-then-poll loop into one blocking call that
 * returns the terminal asset with its `file.id` — the value every downstream
 * step (ad creatives, attachments) actually needs.
 */
export async function buildMediaGroup(description: string) {
	const media = Cli.create("media", { description });
	const spec = filterSpecByTag(nativeSpec as Record<string, unknown>, "Media");
	const generated = await Openapi.generateCommands(
		spec as Openapi.OpenAPISpec,
		whopFetch,
	);

	for (const [name, entry] of generated) {
		if (!("run" in entry)) continue;
		if (name === "generate") {
			media.command(name, buildGenerateCommand(entry));
			continue;
		}
		media.command(name, entry);
	}

	return media;
}

function buildGenerateCommand(entry: {
	description?: string | undefined;
	options?: z.ZodObject<Record<string, z.ZodType>> | undefined;
	run: (c: never) => unknown;
}) {
	const options = (entry.options ?? z.object({})).extend({
		wait: z
			.boolean()
			.optional()
			.describe(
				"Block until generation finishes and return the terminal asset (with file.id when ready)",
			),
		timeout: z.coerce
			.number()
			.default(600)
			.describe("Max seconds to wait with --wait before giving up"),
	});

	return {
		description: entry.description,
		options,
		examples: [
			{
				options: {
					type: "image",
					prompt: "A running club jogging across a bridge at sunrise",
					wait: true,
				},
				description: "Generate an image and wait for the finished file",
			},
			{
				options: {
					type: "video",
					prompt: "Sneaker product spin on a white background",
					duration_seconds: 5,
					wait: true,
				},
				description: "Generate a 5s video and wait",
			},
		],
		run: async (c: RunCtx) => {
			const { wait, timeout, ...body } = c.options;
			const request = Object.fromEntries(
				Object.entries(body).filter(([, value]) => value !== undefined),
			);

			const asset = await createGeneration(c, request);
			if (!wait) return asset;

			return awaitGeneration(c, asset, Number(timeout));
		},
	};
}

async function createGeneration(
	c: RunCtx,
	body: Record<string, unknown>,
): Promise<MediaAsset> {
	const res = await whopFetch(
		new Request("https://api.whop.com/media/generate", {
			method: "POST",
			body: JSON.stringify(body),
		}),
	);
	const payload = (await res.json().catch(() => null)) as
		| (MediaAsset & { message?: string; error?: { deposit_url?: string } })
		| null;

	if (res.ok && payload) return payload;

	if (res.status === 402) {
		const depositUrl = payload?.error?.deposit_url;
		return c.error({
			code: "INSUFFICIENT_BALANCE",
			message: `${payload?.message ?? "Insufficient balance to fund this generation."}${depositUrl ? ` Deposit at ${depositUrl}` : ""}`,
			retryable: true,
			cta: {
				description: "Fund your balance, then retry:",
				commands: [
					{ command: "deposits create", description: "Add funds to your balance" },
				],
			},
		});
	}

	return c.error({
		code: "API_ERROR",
		message: payload?.message ?? `Generation request failed (HTTP ${res.status})`,
		retryable: true,
	});
}

async function awaitGeneration(
	c: RunCtx,
	created: MediaAsset,
	timeoutSeconds: number,
): Promise<MediaAsset> {
	const interactive = !c.agent && process.stdout.isTTY;
	const progress = interactive ? spinner() : null;
	progress?.start(`Generating ${created.media_type ?? "asset"}…`);

	const startedAt = Date.now();
	let asset = created;

	while (asset.status === "processing") {
		if (Date.now() - startedAt > timeoutSeconds * 1000) {
			progress?.stop(`Still processing after ${timeoutSeconds}s.`);
			return c.error({
				code: "WAIT_TIMEOUT",
				message: `Generation ${asset.id} is still processing after ${timeoutSeconds}s. It keeps running server-side — check on it later.`,
				retryable: true,
				cta: {
					description: "Check the asset:",
					commands: [
						{ command: `media get ${asset.id}`, description: "Poll the asset status" },
					],
				},
			});
		}

		await sleep(POLL_INTERVAL_MS);
		progress?.message(
			`Generating ${created.media_type ?? "asset"}… ${Math.round((Date.now() - startedAt) / 1000)}s`,
		);
		asset = await fetchAsset(c, asset.id, progress);
	}

	if (asset.status === "failed") {
		progress?.stop("Generation failed.");
		return c.error({
			code: "GENERATION_FAILED",
			message: `${asset.error_message ?? "Generation failed."} The charge was refunded — adjust the prompt and retry.`,
			retryable: true,
		});
	}

	progress?.stop(
		`Generated ${asset.file?.id ?? asset.id} in ${Math.round((Date.now() - startedAt) / 1000)}s.`,
	);
	return asset;
}

async function fetchAsset(
	c: RunCtx,
	id: string,
	progress: ReturnType<typeof spinner> | null,
): Promise<MediaAsset> {
	const res = await whopFetch(
		new Request(`https://api.whop.com/media/${id}`, { method: "GET" }),
	);
	const payload = (await res.json().catch(() => null)) as
		| (MediaAsset & { message?: string })
		| null;
	if (!res.ok || !payload) {
		progress?.stop("Polling failed.");
		return c.error({
			code: "API_ERROR",
			message:
				payload?.message ?? `Couldn't poll media asset ${id} (HTTP ${res.status})`,
			retryable: true,
		});
	}
	return payload;
}

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
